import React, { useEffect, useMemo, useState } from 'react';
import {
    View,
    ScrollView,
    Text,
    TextInput,
    useWindowDimensions,
    StyleSheet,
} from 'react-native';
import { useTheme } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import { LineChart, ContributionGraph } from 'react-native-chart-kit';

import CustomLabel from '../../../components/CustomLabel/CustomLabel';
import CustomDropDown from '../../../components/CustomDropDown/CustomDropdown';
import { useAnalytics } from '../../../hooks/useAnalytics';
import { MONTHNAMES } from '../../../constants/FormatDate';

const LeavesAnalyticsSection = ({ selectedCompanyCode }) => {
    // -------------------------------------------------------------------------
    // Hooks / theme / i18n
    // -------------------------------------------------------------------------
    const { t } = useTranslation();
    const { colors } = useTheme();
    const styles = makeStyles(colors);
    const { width } = useWindowDimensions();

    // -------------------------------------------------------------------------
    // Analytics API
    // -------------------------------------------------------------------------
    const {
        getApprovedLeaveRequestsPerMonth,
        getApprovedLeaveRequestsPerDay,
        getApprovedLeaveDetailsPerDay,
    } = useAnalytics();

    // -------------------------------------------------------------------------
    // Constants
    // -------------------------------------------------------------------------
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;

    const GAP = 12;
    const LEFT_COL_WIDTH = 520;
    const MID_COL_WIDTH = 985;
    const RIGHT_COL_WIDTH = 220;
    const DETAILS_TILE_WIDTH = MID_COL_WIDTH + RIGHT_COL_WIDTH + GAP;
    const TILE_PADDING = 16;
    const SURFACE_PADDING = 8;
    
    const DASHBOARD_FIXED_WIDTH = LEFT_COL_WIDTH + MID_COL_WIDTH + RIGHT_COL_WIDTH + GAP * 2;

    const leftTileInnerWidth = LEFT_COL_WIDTH - TILE_PADDING * 2;
    const leftSurfaceInnerWidth = leftTileInnerWidth - SURFACE_PADDING * 2;

    const HEATMAP_GRAPH_WIDTH = leftSurfaceInnerWidth - 100;
    const HEATMAP_DAY_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
    const HEATMAP_DAY_LABEL_CELL_WIDTH = 16;
    const HEATMAP_DAY_LABEL_GAP = 7;
    const HEATMAP_DAY_LABELS_WIDTH =
        HEATMAP_DAY_LABELS.length * HEATMAP_DAY_LABEL_CELL_WIDTH +
        (HEATMAP_DAY_LABELS.length - 1) * HEATMAP_DAY_LABEL_GAP;

    // -------------------------------------------------------------------------
    // State
    // -------------------------------------------------------------------------
    const [leavesYear, setLeavesYear] = useState(`${currentYear}`);
    const [leavesMonth, setLeavesMonth] = useState(`${currentMonth}`);

    const [leaveRequestsSeries, setLeaveRequestsSeries] = useState([]);
    const [leaveRequestsDailySeries, setLeaveRequestsDailySeries] = useState([]);
    const [leaveDetails, setLeaveDetails] = useState([]);

    const [leavesLoading, setLeavesLoading] = useState(false);
    const [leavesError, setLeavesError] = useState('');

    const [leavesDailyLoading, setLeavesDailyLoading] = useState(false);
    const [leavesDailyError, setLeavesDailyError] = useState('');

    const [leaveDetailsLoading, setLeaveDetailsLoading] = useState(false);
    const [leaveDetailsError, setLeaveDetailsError] = useState('');

    const [selectedDay, setSelectedDay] = useState(null);
    const [leaveSearch, setLeaveSearch] = useState('');

    // -------------------------------------------------------------------------
    // Helpers
    // -------------------------------------------------------------------------
    const renderCenteredMessage = (styles, message) => (
        <View style={styles.centerBox}>
            <CustomLabel value={message} type="ASSM" />
        </View>
    );

    // -------------------------------------------------------------------------
    // Data fetching
    // -------------------------------------------------------------------------

    // Load monthly approved leave requests for the selected year.
    useEffect(() => {
        const loadLeavesRequestsPerMonth = async () => {
            if (!selectedCompanyCode) return;

            setLeavesLoading(true);
            setLeavesError('');

            try {
                const res = await getApprovedLeaveRequestsPerMonth(selectedCompanyCode, Number(leavesYear));
                const payload = res?.data ?? res;
                setLeaveRequestsSeries(Array.isArray(payload) ? payload : []);
            } catch (e) {
                setLeaveRequestsSeries([]);
                setLeavesError(e?.message || 'Failed to load leave requests');
            } finally {
                setLeavesLoading(false);
            }
        };

        loadLeavesRequestsPerMonth();
    }, [selectedCompanyCode, leavesYear]);

    // Load daily approved leave requests for the selected month/year.
    useEffect(() => {
        const loadLeavesRequestsPerDay = async () => {
            if (!selectedCompanyCode) return;

            setLeavesDailyLoading(true);
            setLeavesDailyError('');

            try {
                const res = await getApprovedLeaveRequestsPerDay(
                    selectedCompanyCode,
                    Number(leavesYear),
                    Number(leavesMonth)
                );
                const payload = res?.data ?? res;
                setLeaveRequestsDailySeries(Array.isArray(payload) ? payload : []);
            } catch (e) {
                setLeaveRequestsDailySeries([]);
                setLeavesDailyError(e?.message || 'Failed to load daily leave requests');
            } finally {
                setLeavesDailyLoading(false);
            }
        };

        loadLeavesRequestsPerDay();
    }, [selectedCompanyCode, leavesYear, leavesMonth]);

    // Reset selected day state when the selected period/company changes.
    useEffect(() => {
        setSelectedDay(null);
        setLeaveDetails([]);
        setLeaveDetailsError('');
        setLeaveSearch('');
    }, [leavesYear, leavesMonth, selectedCompanyCode]);

    // Load leave details for the selected day from the heatmap.
    useEffect(() => {
        const loadLeaveDetails = async () => {
            if (!selectedCompanyCode || !selectedDay?.date) {
                setLeaveDetails([]);
                return;
            }

            setLeaveDetailsLoading(true);
            setLeaveDetailsError('');

            try {
                const res = await getApprovedLeaveDetailsPerDay(selectedCompanyCode, selectedDay.date);
                const payload = res?.data ?? res;
                setLeaveDetails(Array.isArray(payload) ? payload : []);
            } catch (e) {
                setLeaveDetails([]);
                setLeaveDetailsError(e?.message || 'Failed to load leave details');
            } finally {
                setLeaveDetailsLoading(false);
            }
        };

        loadLeaveDetails();
    }, [selectedCompanyCode, selectedDay]);

    // Clear search whenever a different day is selected.
    useEffect(() => {
        setLeaveSearch('');
    }, [selectedDay]);

    // -------------------------------------------------------------------------
    // Derived data
    // -------------------------------------------------------------------------
    const YEAR_OPTIONS = useMemo(() => {
        const years = [];
        const COUNT = 200;

        for (let i = 0; i < COUNT; i++) {
            const y = currentYear - i;
            years.push({ label: `${y}`, value: `${y}` });
        }

        return years;
    }, [currentYear]);

    const MONTH_OPTIONS = useMemo(() => {
        return MONTHNAMES.map((name, index) => ({
            label: t(`common:${name}`),
            value: `${index + 1}`,
        }));
    }, [t]);

    const leaveLineData = useMemo(() => {
        const values = Array(12).fill(0);

        (leaveRequestsSeries || []).forEach((x) => {
            const monthNo = Number(x?.label);
            const count = Number(x?.value ?? 0);

            if (monthNo >= 1 && monthNo <= 12) {
                values[monthNo - 1] = count;
            }
        });

        return {
            labels: MONTHNAMES.map((name) => t(`common:${name}`)),
            datasets: [{ data: values }],
        };
    }, [leaveRequestsSeries, t]);

    const leaveContributionData = useMemo(() => {
        return (leaveRequestsDailySeries || []).map((x) => ({
            date: x?.label,
            count: Number(x?.value ?? 0),
        }));
    }, [leaveRequestsDailySeries]);

    const leaveContributionEndDate = useMemo(() => {
        return new Date(Number(leavesYear), Number(leavesMonth), 0);
    }, [leavesYear, leavesMonth]);

    const leaveContributionNumDays = useMemo(() => {
        return new Date(Number(leavesYear), Number(leavesMonth), 0).getDate();
    }, [leavesYear, leavesMonth]);

    const heatmapWeekLabels = useMemo(() => {
        const totalWeeks = Math.ceil(leaveContributionNumDays / 7);
        return Array.from({ length: totalWeeks }, (_, index) => `Week ${index + 1}`);
    }, [leaveContributionNumDays]);

    const selectedDayFormatted = useMemo(() => {
        if (!selectedDay?.date) return '';

        return new Date(selectedDay.date).toLocaleDateString(undefined, {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        });
    }, [selectedDay]);

    // Search across visible leave detail fields, including formatted duration.
    const filteredLeaveDetails = useMemo(() => {
        const q = leaveSearch.trim().toLowerCase();

        if (!q) return leaveDetails;

        return (leaveDetails || []).filter((item) => {
            const employeeId = String(item?.employeeId ?? '').toLowerCase();
            const employeeName = String(item?.employeeName ?? '').toLowerCase();
            const startDate = String(item?.startDate ?? '').toLowerCase();
            const endDate = String(item?.endDate ?? '').toLowerCase();
            const leaveType = String(item?.leaveType ?? '').toLowerCase();

            const durationValue = Number(item?.duration ?? 0);
            const durationLabel = durationValue === 1 ? '1 day' : `${durationValue} days`;

            const isNumericQuery = /^\d+$/.test(q);

            return (
                employeeId.includes(q) ||
                employeeName.includes(q) ||
                startDate.includes(q) ||
                endDate.includes(q) ||
                leaveType.includes(q) ||
                durationLabel.includes(q) ||
                (isNumericQuery && durationValue === Number(q))
            );
        });
    }, [leaveDetails, leaveSearch]);

    const lineChartConfig = useMemo(() => ({
        backgroundGradientFrom: 'transparent',
        backgroundGradientTo: 'transparent',
        color: (opacity = 1) => `rgba(0, 128, 0, ${opacity})`,
        labelColor: () => colors.text,
        propsForLabels: { fontSize: 10.5, fontWeight: '600' },
        propsForBackgroundLines: { stroke: 'transparent' },
        decimalPlaces: 0,
        paddingRight: 30,
    }), [colors.text]);

    const contributionChartConfig = useMemo(() => ({
        backgroundGradientFrom: '#ffffff',
        backgroundGradientTo: '#ffffff',
        color: (opacity = 1) => `rgba(0, 100, 0, ${Math.max(opacity, 0.02)})`,
        labelColor: (opacity = 1) => `rgba(15, 23, 42, ${opacity})`,
    }), []);

    // -------------------------------------------------------------------------
    // Render
    // -------------------------------------------------------------------------
    return (
        <View style={styles.additionalCard}>
            {!selectedCompanyCode ? (
                renderCenteredMessage(styles, t('common:loadingselectedCompany'))
            ) : (
                <ScrollView
                    horizontal={width < DASHBOARD_FIXED_WIDTH}
                    showsHorizontalScrollIndicator={width < DASHBOARD_FIXED_WIDTH}
                    contentContainerStyle={{ flexGrow: 1 }}
                >
                    <ScrollView
                        nestedScrollEnabled
                        showsVerticalScrollIndicator
                        contentContainerStyle={{ flexGrow: 1 }}
                    >
                        <View style={[styles.dashboardOuter, { minWidth: DASHBOARD_FIXED_WIDTH }]}>
                            <View style={[styles.dashboardRow, { flexWrap: 'nowrap' }]}>
                                {/* -----------------------------------------------------------------
                                    LEFT COLUMN
                                    ----------------------------------------------------------------- */}
                                <View style={[styles.col, { minWidth: LEFT_COL_WIDTH }]}>
                                    {/* Monthly leave requests line chart */}
                                    <View style={[styles.tile, styles.leavesTile, { backgroundColor: 'white' }]}>
                                        <View style={styles.halfTileHeader}>
                                            <CustomLabel
                                                value={t('navigate:leaveRequestsPerMonth') || 'Leave Requests per Month'}
                                                type="LABEL"
                                            />

                                            <View style={{ width: Math.min(140, LEFT_COL_WIDTH * 0.45) }}>
                                                <CustomDropDown
                                                    defValue={leavesYear}
                                                    options={YEAR_OPTIONS}
                                                    setValue={setLeavesYear}
                                                    type="MAININPUT"
                                                    label={t('common:year') || 'Year'}
                                                />
                                            </View>
                                        </View>

                                        {leavesLoading ? (
                                            renderCenteredMessage(styles, t('common:loading'))
                                        ) : leavesError ? (
                                            renderCenteredMessage(styles, leavesError)
                                        ) : (
                                            <View style={styles.lineChartSurface}>
                                                <LineChart
                                                    data={leaveLineData}
                                                    width={600}
                                                    height={247}
                                                    chartConfig={lineChartConfig}
                                                    verticalLabelRotation={20}
                                                    bezier
                                                    style={{ marginTop: 8, marginLeft: -30 }}
                                                />
                                            </View>
                                        )}
                                    </View>

                                    {/* Daily leave heatmap */}
                                    <View style={[styles.tile, styles.heatmapTile, { backgroundColor: 'white' }]}>
                                        <View style={styles.halfTileHeader}>
                                            <CustomLabel
                                                value={t('navigate:leaveRequestsHeatmap')}
                                                type="LABEL"
                                            />

                                            <View style={{ width: Math.min(140, LEFT_COL_WIDTH * 0.45) }}>
                                                <CustomDropDown
                                                    defValue={leavesMonth}
                                                    options={MONTH_OPTIONS}
                                                    setValue={setLeavesMonth}
                                                    type="MAININPUT"
                                                    label={t('common:month') || 'Month'}
                                                />
                                            </View>
                                        </View>

                                        {leavesDailyLoading ? (
                                            renderCenteredMessage(styles, t('common:loading'))
                                        ) : leavesDailyError ? (
                                            renderCenteredMessage(styles, leavesDailyError)
                                        ) : (
                                            <View style={styles.chartSurface}>
                                                {/* Day labels above the contribution graph */}
                                                <View style={styles.heatmapHeaderRow}>
                                                    <View style={styles.heatmapLeftSpacer} />
                                                    <View
                                                        style={[
                                                            styles.heatmapDayLabelsRow,
                                                            { width: HEATMAP_DAY_LABELS_WIDTH, marginLeft: 120 },
                                                        ]}
                                                    >
                                                        {HEATMAP_DAY_LABELS.map((dayLabel, index) => (
                                                            <View
                                                                key={`${dayLabel}-${index}`}
                                                                style={[
                                                                    styles.heatmapDayLabelCell,
                                                                    {
                                                                        width: HEATMAP_DAY_LABEL_CELL_WIDTH,
                                                                        marginRight:
                                                                            index === HEATMAP_DAY_LABELS.length - 1
                                                                                ? 0
                                                                                : HEATMAP_DAY_LABEL_GAP,
                                                                    },
                                                                ]}
                                                            >
                                                                <Text style={styles.heatmapDayLabelText}>{dayLabel}</Text>
                                                            </View>
                                                        ))}
                                                    </View>
                                                </View>

                                                <View style={styles.heatmapMainRow}>
                                                    {/* Week labels aligned to the left of the heatmap */}
                                                    <View style={styles.heatmapWeekLabelsCol}>
                                                        {heatmapWeekLabels.map((weekLabel) => (
                                                            <View key={weekLabel} style={styles.heatmapWeekLabelCell}>
                                                                <Text style={styles.heatmapWeekLabelText}>{weekLabel}</Text>
                                                            </View>
                                                        ))}
                                                    </View>

                                                    <View style={styles.heatmapWrap}>
                                                        <ContributionGraph
                                                            values={leaveContributionData}
                                                            horizontal={false}
                                                            endDate={leaveContributionEndDate}
                                                            numDays={leaveContributionNumDays}
                                                            width={HEATMAP_GRAPH_WIDTH}
                                                            height={125}
                                                            chartConfig={contributionChartConfig}
                                                            gutterSize={2}
                                                            onDayPress={(value) => setSelectedDay(value)}
                                                            showMonthLabels={false}
                                                        />
                                                    </View>
                                                </View>

                                                {/* Compact selected-day status */}
                                                <View style={styles.selectedDayCardWrap}>
                                                    <View style={styles.selectedDayCard}>
                                                        <View style={styles.selectedDayCompactRow}>
                                                            <View style={styles.selectedDayHeaderRow}>
                                                                <View style={selectedDay ? styles.selectedDayAccent : styles.selectedDayAccentMuted} />
                                                                <Text style={styles.selectedDayHeaderText}>
                                                                    {selectedDay
                                                                        ? t('navigate:showingEmployeesOnLeave')
                                                                        : t('navigate:tapDayForDetails')}
                                                                </Text>
                                                            </View>
                                                        </View>
                                                    </View>
                                                </View>
                                            </View>
                                        )}
                                    </View>
                                </View>

                                {/* -----------------------------------------------------------------
                                    RIGHT / DETAILS COLUMN
                                    ----------------------------------------------------------------- */}
                                <View style={[styles.col, { flexGrow: 2.1, minWidth: MID_COL_WIDTH }]}>
                                    <View
                                        style={[
                                            styles.tile,
                                            styles.detailsTile,
                                            {
                                                backgroundColor: 'white',
                                                width: DETAILS_TILE_WIDTH,
                                            },
                                        ]}
                                    >
                                        {/* Details header */}
                                        <View style={styles.detailsHeaderWrap}>
                                            <View style={styles.detailsHeaderTop}>
                                                <View>
                                                    <CustomLabel
                                                        value={t('navigate:leaveDetailsForSelectedDay') || 'Leave Details'}
                                                        type="LABEL"
                                                    />
                                                    <Text style={styles.detailsSubtitle}>
                                                        {selectedDay
                                                            ? selectedDayFormatted
                                                            : (t('navigate:tapDayForDetails') || 'Select a day from the heatmap to view leave details')}
                                                    </Text>
                                                </View>

                                                <View style={styles.detailsCountBadge}>
                                                    <Text style={styles.detailsCountText}>
                                                        {selectedDay ? `${selectedDay.count}` : '-'}
                                                    </Text>
                                                </View>
                                            </View>

                                            {selectedDay ? (
                                                <View style={styles.detailsSelectedMeta}>
                                                    <View style={styles.detailsMetaPill}>
                                                        <View style={styles.detailsMetaDot} />
                                                        <Text style={styles.detailsMetaText}>
                                                            {t('common:date') || 'Date'}: {selectedDayFormatted}
                                                        </Text>
                                                    </View>

                                                    <View style={styles.detailsMetaPillSoft}>
                                                        <Text style={styles.detailsMetaTextSoft}>
                                                            {t('navigate:approvedLeaves') || 'Approved leaves'}: {selectedDay.count ?? 0}
                                                        </Text>
                                                    </View>
                                                </View>
                                            ) : null}
                                        </View>

                                        {/* Empty state before a day is selected */}
                                        {!selectedDay ? (
                                            <View style={styles.emptyStateWrap}>
                                                <View style={styles.emptyStateIconCircle}>
                                                    <Text style={styles.emptyStateIcon}>📅</Text>
                                                </View>
                                                <Text style={styles.emptyStateTitle}>
                                                    {t('navigate:tapDayForDetails') || 'Tap a day for details'}
                                                </Text>
                                                <Text style={styles.emptyStateText}>
                                                    Select any highlighted day in the heatmap to display the employees with approved leave requests for that date.
                                                </Text>
                                            </View>
                                        ) : leaveDetailsLoading ? (
                                            renderCenteredMessage(styles, t('common:loading'))
                                        ) : leaveDetailsError ? (
                                            renderCenteredMessage(styles, leaveDetailsError)
                                        ) : leaveDetails.length === 0 ? (
                                            <View style={styles.emptyStateWrap}>
                                                <View style={styles.emptyStateIconCircleMuted}>
                                                    <Text style={styles.emptyStateIcon}>ℹ️</Text>
                                                </View>
                                                <Text style={styles.emptyStateTitle}>
                                                    {t('navigate:noData') || 'No data'}
                                                </Text>
                                                <Text style={styles.emptyStateText}>
                                                    No approved leave records were found for the selected day.
                                                </Text>
                                            </View>
                                        ) : (
                                            <>
                                                {/* Search within leave details */}
                                                <View style={styles.tableSearchWrap}>
                                                    <TextInput
                                                        value={leaveSearch}
                                                        onChangeText={setLeaveSearch}
                                                        placeholder={t('navigate:searchLeaveDetails')}
                                                        placeholderTextColor="#737d75"
                                                        style={styles.tableSearchInput}
                                                    />
                                                </View>

                                                {/* Leave details table */}
                                                <View style={styles.tableCard}>
                                                    <View style={styles.tableHeaderRow}>
                                                        <Text style={[styles.tableHeaderCell, styles.idCell]}>Employee ID</Text>
                                                        <Text style={[styles.tableHeaderCell, styles.nameCell]}>Employee Name</Text>
                                                        <Text style={[styles.tableHeaderCell, styles.dateCell]}>Start Date</Text>
                                                        <Text style={[styles.tableHeaderCell, styles.dateCell]}>End Date</Text>
                                                        <Text style={[styles.tableHeaderCell, styles.durationCell]}>Duration</Text>
                                                        <Text style={[styles.tableHeaderCell, styles.typeCell]}>Type</Text>
                                                    </View>

                                                    <ScrollView
                                                        style={styles.tableScroll}
                                                        nestedScrollEnabled
                                                        showsVerticalScrollIndicator
                                                    >
                                                        {filteredLeaveDetails.length === 0 ? (
                                                            <View style={styles.tableNoResultsWrap}>
                                                                <Text style={styles.tableNoResultsText}>
                                                                    {t('navigate:noResults') || 'No matching results found'}
                                                                </Text>
                                                            </View>
                                                        ) : (
                                                            filteredLeaveDetails.map((item, index) => (
                                                                <View
                                                                    key={`${item.employeeId}-${item.startDate}-${item.endDate}-${index}`}
                                                                    style={[
                                                                        styles.tableRow,
                                                                        index % 2 === 0 ? styles.tableRowEven : styles.tableRowOdd,
                                                                    ]}
                                                                >
                                                                    <Text style={[styles.tableCell, styles.idCell]}>
                                                                        {item.employeeId}
                                                                    </Text>

                                                                    <Text
                                                                        style={[styles.tableCell, styles.nameCell]}
                                                                        numberOfLines={1}
                                                                    >
                                                                        {item.employeeName}
                                                                    </Text>

                                                                    <Text style={[styles.tableCell, styles.dateCell]}>
                                                                        {item.startDate}
                                                                    </Text>

                                                                    <Text style={[styles.tableCell, styles.dateCell]}>
                                                                        {item.endDate}
                                                                    </Text>

                                                                    <Text style={[styles.tableCell, styles.durationCell]}>
                                                                        {item.duration === 1 ? '1 day' : `${item.duration ?? 0} days`}
                                                                    </Text>

                                                                    <Text
                                                                        style={[styles.tableCell, styles.typeCell]}
                                                                        numberOfLines={1}
                                                                    >
                                                                        {item.leaveType}
                                                                    </Text>
                                                                </View>
                                                            ))
                                                        )}
                                                    </ScrollView>
                                                </View>
                                            </>
                                        )}
                                    </View>
                                </View>
                            </View>
                        </View>
                    </ScrollView>
                </ScrollView>
            )}
        </View>
    );
};

const makeStyles = (colors) =>
    StyleSheet.create({
        additionalCard: {
            flex: 1,
            marginVertical: 10,
            marginHorizontal: 30,
            padding: 12,
            borderRadius: 8,
            backgroundColor: colors.card ?? '#fff',
        },

        centerBox: {
            flex: 1,
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
        },

        dashboardOuter: {
            flex: 1,
            alignItems: 'stretch',
        },

        dashboardRow: {
            flex: 1,
            width: '100%',
            flexDirection: 'row',
            alignItems: 'stretch',
            justifyContent: 'space-between',
            gap: 12,
        },

        col: {
            flex: 1,
            height: '100%',
            gap: 12,
        },

        tile: {
            borderWidth: 1,
            borderColor: colors.border ?? '#E5E7EB',
            borderRadius: 16,
            padding: 16,
            backgroundColor: colors.card ?? '#fcf9f9',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        },

        halfTileHeader: {
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 10,
        },

        chartSurface: {
            marginTop: 12,
            alignSelf: 'stretch',
            paddingVertical: 10,
            paddingHorizontal: 8,
            paddingBottom: 0,
            borderRadius: 12,
            backgroundColor: 'white',
            borderColor: '#E3EAFD',
            minHeight: 210,
        },

        lineChartSurface: {
            marginTop: 1,
            alignSelf: 'stretch',
            paddingTop: 10,
            paddingHorizontal: 8,
            paddingBottom: 1,
            borderRadius: 12,
            backgroundColor: 'white',
            borderColor: '#E3EAFD',
        },

        leavesTile: {
            minHeight: 350,
        },

        heatmapTile: {
            minHeight: 307,
            width: '100%',
        },

        detailsTile: {
            minHeight: 670,
            padding: 18,
            backgroundColor: '#FCFEFC',
            borderColor: colors.border ?? '#E5E7EB',
        },

        heatmapHeaderRow: {
            flexDirection: 'row',
            alignItems: 'center',
            marginBottom: 6,
        },

        heatmapLeftSpacer: {
            width: 52,
            marginRight: 6,
        },

        heatmapDayLabelsRow: {
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'flex-start',
        },

        heatmapDayLabelCell: {
            alignItems: 'center',
            justifyContent: 'center',
        },

        heatmapDayLabelText: {
            fontSize: 11,
            fontWeight: '800',
            color: '#5B6B5C',
            textTransform: 'uppercase',
        },

        heatmapMainRow: {
            flexDirection: 'row',
            alignItems: 'flex-start',
        },

        heatmapWeekLabelsCol: {
            width: 52,
            marginRight: 6,
            paddingTop: 2,
        },

        heatmapWeekLabelCell: {
            height: 24,
            justifyContent: 'center',
            alignItems: 'flex-start',
        },

        heatmapWeekLabelText: {
            fontSize: 10,
            fontWeight: '700',
            color: '#6B7A6C',
        },

        selectedDayCardWrap: {
            marginTop: 7,
            width: '100%',
            alignItems: 'flex-end',
        },

        selectedDayCard: {
            paddingVertical: 8,
            paddingHorizontal: 10,
            borderRadius: 12,
            backgroundColor: '#F8FBF8',
            borderWidth: 1,
            borderColor: '#DDEBDD',
            boxShadow: '0 2px 8px rgba(16, 24, 40, 0.05)',
            alignSelf: 'flex-end',
            maxWidth: '100%',
        },

        selectedDayCompactRow: {
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 10,
            minHeight: 32,
        },

        selectedDayCompactLeft: {
            flex: 1,
            minWidth: 0,
        },

        selectedDayHeaderRow: {
            flexDirection: 'row',
            alignItems: 'center',
            marginBottom: 0,
            flexShrink: 1,
        },

        selectedDayAccent: {
            width: 8,
            height: 8,
            borderRadius: 999,
            backgroundColor: '#0A7A2F',
            marginRight: 8,
        },

        selectedDayAccentMuted: {
            width: 8,
            height: 8,
            borderRadius: 999,
            backgroundColor: '#B8C4B8',
            marginRight: 8,
        },

        selectedDayHeaderText: {
            fontSize: 12,
            fontWeight: '700',
            color: '#5B6B5C',
            flexShrink: 1,
        },

        selectedDayValueBadge: {
            minWidth: 34,
            height: 28,
            paddingHorizontal: 10,
            borderRadius: 999,
            backgroundColor: '#E7F6EA',
            borderWidth: 1,
            borderColor: '#CBE8D1',
            alignItems: 'center',
            justifyContent: 'center',
        },

        selectedDayValueText: {
            fontSize: 13,
            fontWeight: '800',
            color: '#0A7A2F',
        },

        heatmapWrap: {
            width: '100%',
            alignItems: 'center',
        },

        detailsHeaderWrap: {
            marginBottom: 14,
            paddingBottom: 12,
            borderBottomWidth: 1,
            borderBottomColor: '#E8F1E8',
        },

        detailsHeaderTop: {
            flexDirection: 'row',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 12,
        },

        detailsSubtitle: {
            marginTop: 6,
            fontSize: 13,
            lineHeight: 18,
            color: colors.text,
            opacity: 0.7,
            maxWidth: 430,
        },

        detailsCountBadge: {
            minWidth: 52,
            height: 36,
            paddingHorizontal: 12,
            borderRadius: 999,
            backgroundColor: '#E7F6EA',
            borderWidth: 1,
            borderColor: '#CBE8D1',
            alignItems: 'center',
            justifyContent: 'center',
        },

        detailsCountText: {
            fontSize: 15,
            fontWeight: '800',
            color: '#0A7A2F',
        },

        detailsSelectedMeta: {
            marginTop: 12,
            flexDirection: 'row',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 8,
        },

        detailsMetaPill: {
            flexDirection: 'row',
            alignItems: 'center',
            paddingVertical: 8,
            paddingHorizontal: 12,
            borderRadius: 999,
            backgroundColor: '#F3FBF4',
            borderWidth: 1,
            borderColor: '#DDEBDD',
        },

        detailsMetaDot: {
            width: 8,
            height: 8,
            borderRadius: 999,
            backgroundColor: '#0A7A2F',
            marginRight: 8,
        },

        detailsMetaText: {
            fontSize: 12,
            fontWeight: '700',
            color: '#35523A',
        },

        detailsMetaPillSoft: {
            paddingVertical: 8,
            paddingHorizontal: 12,
            borderRadius: 999,
            backgroundColor: '#FAFCFA',
            borderWidth: 1,
            borderColor: '#E8F1E8',
        },

        detailsMetaTextSoft: {
            fontSize: 12,
            fontWeight: '700',
            color: '#5B6B5C',
        },

        emptyStateWrap: {
            flex: 1,
            minHeight: 420,
            alignItems: 'center',
            justifyContent: 'center',
            paddingHorizontal: 28,
        },

        emptyStateIconCircle: {
            width: 72,
            height: 72,
            borderRadius: 999,
            backgroundColor: '#EAF7EC',
            borderWidth: 1,
            borderColor: '#D4EED9',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 16,
        },

        emptyStateIconCircleMuted: {
            width: 72,
            height: 72,
            borderRadius: 999,
            backgroundColor: '#F5F7F5',
            borderWidth: 1,
            borderColor: '#E5EAE5',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 16,
        },

        emptyStateIcon: {
            fontSize: 28,
        },

        emptyStateTitle: {
            fontSize: 18,
            fontWeight: '800',
            color: colors.text,
            marginBottom: 8,
            textAlign: 'center',
        },

        emptyStateText: {
            fontSize: 13,
            lineHeight: 20,
            color: colors.text,
            opacity: 0.7,
            textAlign: 'center',
            maxWidth: 420,
        },

        tableSearchWrap: {
            marginBottom: 12,
        },

        tableSearchInput: {
            height: 42,
            borderWidth: 1,
            borderColor: '#D7E3D8',
            borderRadius: 10,
            paddingHorizontal: 12,
            backgroundColor: '#FFFFFF',
            color: colors.text,
            fontSize: 13,
        },

        tableCard: {
            flex: 1,
            borderWidth: 1,
            borderColor: '#E6EFE6',
            borderRadius: 14,
            overflow: 'hidden',
            backgroundColor: '#FFFFFF',
        },

        tableHeaderRow: {
            flexDirection: 'row',
            alignItems: 'center',
            minHeight: 48,
            paddingHorizontal: 10,
            backgroundColor: '#DFF2E2',
            borderBottomWidth: 1,
            borderBottomColor: '#CFE7D4',
        },

        tableHeaderCell: {
            fontSize: 12,
            fontWeight: '800',
            color: '#24422A',
            paddingHorizontal: 8,
        },

        tableScroll: {
            maxHeight: 400,
        },

        tableRow: {
            flexDirection: 'row',
            alignItems: 'center',
            minHeight: 52,
            paddingHorizontal: 10,
            borderBottomWidth: 1,
            borderBottomColor: '#EEF3EE',
        },

        tableRowEven: {
            backgroundColor: '#FFFFFF',
        },

        tableRowOdd: {
            backgroundColor: '#FAFCFA',
        },

        tableCell: {
            fontSize: 13,
            color: colors.text,
            paddingVertical: 12,
            paddingHorizontal: 8,
        },

        tableNoResultsWrap: {
            paddingVertical: 24,
            paddingHorizontal: 16,
            alignItems: 'center',
            justifyContent: 'center',
        },

        tableNoResultsText: {
            fontSize: 13,
            color: colors.text,
            opacity: 0.7,
        },

        idCell: {
            flex: 1.1,
        },

        nameCell: {
            flex: 2.1,
        },

        dateCell: {
            flex: 1.35,
        },

        typeCell: {
            flex: 1.7,
        },

        durationCell: {
            flex: 1.1,
        },

        sideSpacerTile: {
            flex: 1,
            minHeight: 692,
            height: 692,
            borderWidth: 1,
            borderColor: 'transparent',
            borderRadius: 16,
            backgroundColor: 'transparent',
        },

        sideSpacerInner: {
            flex: 1,
            alignItems: 'center',
            justifyContent: 'center',
            opacity: 0.25,
        },

        sideSpacerText: {
            fontSize: 14,
            fontWeight: '700',
            color: colors.text,
        },
    });

export default LeavesAnalyticsSection;